import { useState, useEffect, useCallback, useRef } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { mdiAccount, mdiBellRingOutline, mdiCalendar, mdiCheckCircle, mdiClockOutline, mdiFileTreeOutline, mdiFlagOutline, mdiLoading, mdiMapMarker, mdiShimmer, mdiTag } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { useDebounce } from '../../hooks/useDebounce'
import { Input } from '../ui/input'
import { MarkdownEditor } from '../MarkdownEditor'
import { Badge } from '../ui/badge'
import { VisibilityBadge } from '../VisibilityBadge'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select'
import { IssueTargetLink, useIssueTargetLabel, type TargetType } from './IssueTargetLink'
import { IssueSourceCard } from './IssueSourceCard'
import { formatIssueReference } from '../../lib/issue-reference'
import { SuggestionProofCard } from './SuggestionProofCard'
import { ReconciliationCard, type ReconciliationProps } from './ReconciliationCard'
import { ClaimDossierCard, type ClaimDossier } from './ClaimDossierCard'
import { DeclarationCard } from './DeclarationCard'
import type { DeclarationProperties } from './declaration-fields'
import { IssueComments } from './IssueComments'
import { CloseStatusBadge } from '../tickets/CloseStatusBadge'
import { EntityConversations } from '../conversations/EntityConversations'
import { ClosedTicketThreads } from './ClosedTicketThreads'
import { TicketRuleCard } from './TicketRuleCard'
import { EmailDeliveryChip, type EmailDelivery } from '../workorders/EmailDeliveryChip'
import { ActionComposeDialog } from './ActionComposeDialog'
import { AddToWorksPlanButton } from './AddToWorksPlanButton'
import { IssueChildrenCard } from './IssueChildrenCard'
import { ScenarioPanel } from '../scenarios/ScenarioPanel'
import { CloseIssueDialog, closeNeedsDialog } from './CloseIssueDialog'
import { IssueNoticesCard, NoticeDialog } from './NoticeDialog'
import { errorCode, followUpDue } from './issueFamily'

/** Actions whose button opens the draft-and-confirm compose dialog rather than
 * closing the ticket (mirror of the backend send_email registry, actions.go). */
const SEND_EMAIL_ACTIONS = new Set([
    'order_diagnosis',
    'request_document',
    'chase_provider',
    'renegotiate',
    'investigate_replacement',
    'change_provider',
    'dunning',
    'order_works',
    'request_quotes',
])
import { Button } from '../ui/button'
import { Link, useNavigate } from '@tanstack/react-router'
import { useTicketSubtypes, subtypeLabel } from '../../hooks/useTicketSubtypes'

const PRIORITY_CONFIG: Record<number, { color: string; tKey: string }> = {
    1: { color: 'bg-red-500', tKey: 'priority.critical' },
    2: { color: 'bg-orange-500', tKey: 'priority.high' },
    3: { color: 'bg-yellow-500', tKey: 'priority.medium' },
    4: { color: 'bg-blue-500', tKey: 'priority.low' },
    5: { color: 'bg-gray-400', tKey: 'priority.lowest' },
}

const STATUS_OPTIONS = [
    { value: 'draft' },
    { value: 'open' },
    { value: 'in_progress' },
    { value: 'closed' },
] as const

/** Structured address payload carried by data_consistency issue properties
 * (jobsrun/address_consistency.go — ISO 20022 subset + raw). */
type MismatchAddress = {
    streetName?: string
    buildingNumber?: string
    postCode?: string
    townName?: string
    raw?: string
}

function formatMismatchAddress(a: MismatchAddress | undefined): string {
    if (!a) return ''
    const street = [a.buildingNumber, a.streetName].filter(Boolean).join(' ')
    const city = [a.postCode, a.townName].filter(Boolean).join(' ')
    const composed = [street, city].filter(Boolean).join(', ')
    return composed || a.raw || ''
}

function formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString()
}

function toDateInputValue(dateString: string | null | undefined): string {
    if (!dateString) return ''
    const d = new Date(dateString)
    return d.toISOString().split('T')[0]
}

type IssueBodyProps = {
    orgCode: string
    issueUid: string
    /** Sheet-mode rendering: single-column layout, no page-level chrome. */
    compact?: boolean
    /** Slot rendered to the left of the title (e.g. page-level back button). */
    titleLeading?: React.ReactNode
}

export function IssueBody({ orgCode, issueUid, compact = false, titleLeading }: IssueBodyProps) {
    const queryClient = useQueryClient()
    const { t, i18n } = useTranslation('issues')
    const navigate = useNavigate()

    // Accept a coverage suggestion into a claim dossier (spec 2026-07-17-01,
    // lot 4) and jump to the new claim.
    const openClaim = $api.useMutation('post', '/api/v1/organizations/{orgCode}/issues/{issueUid}/claim', {
        onSuccess: (claim: { uid?: string }) => {
            queryClient.invalidateQueries()
            if (claim?.uid) {
                navigate({ to: '/$org/issues/$uid', params: { org: orgCode, uid: claim.uid } })
            }
        },
    })

    // Catalogue-driven close-action (spec 2026-07-18-01): a ticket's subtype
    // declares its default_action; resolving it records the outcome and closes
    // the ticket (or, for open_claim, spawns and navigates to the claim).
    const { byCode } = useTicketSubtypes(orgCode)
    const resolveAction = $api.useMutation('post', '/api/v1/organizations/{orgCode}/issues/{issueUid}/action', {
        onSuccess: (res: { issue?: { uid?: string }; created?: boolean }) => {
            queryClient.invalidateQueries()
            if (res?.created && res.issue?.uid) {
                navigate({ to: '/$org/issues/$uid', params: { org: orgCode, uid: res.issue.uid } })
            }
        },
    })

    const { data: issue, isLoading } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/issues/{issueUid}',
        {
            params: { path: { orgCode, issueUid } },
        },
    )

    const { data: usersData } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/users',
        {
            params: {
                path: { orgCode },
                query: { type: 'user', limit: 100 },
            },
        },
    )
    const users = usersData?.data ?? []

    const [editingTitle, setEditingTitle] = useState(false)
    const [titleValue, setTitleValue] = useState('')
    const [descriptionValue, setDescriptionValue] = useState('')
    const [editingDescription, setEditingDescription] = useState(false)
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
    const [composeOpen, setComposeOpen] = useState(false)
    // Closing a parent with open children, or a step awaiting its proof,
    // goes through its dialog (spec 2026-10-27-19 §§ 2, 2b).
    const [closeOpen, setCloseOpen] = useState(false)
    // A notify_occupants step opens the notice dialog before it closes.
    const [noticeOpen, setNoticeOpen] = useState(false)
    // The chase of a due follow-up opens the compose dialog on its draft.
    const [chasing, setChasing] = useState(false)
    const titleInputRef = useRef<HTMLInputElement>(null)
    const isMountedRef = useRef(true)

    const debouncedDescription = useDebounce(descriptionValue, 1000)

    useEffect(() => {
        if (issue) {
            if (!editingTitle) setTitleValue(issue.title)
            if (!editingDescription) setDescriptionValue(issue.description || '')
        }
    }, [issue, editingTitle, editingDescription])

    useEffect(() => {
        if (editingTitle && titleInputRef.current) {
            titleInputRef.current.focus()
            titleInputRef.current.select()
        }
    }, [editingTitle])

    // Description focus is now handled by MarkdownEditor's own `autoFocus`
    // prop (BlockNote's contenteditable can't be focused via a plain ref).

    const invalidateIssues = useCallback(() => {
        queryClient.invalidateQueries({
            predicate: (query) => {
                const queryKey = query.queryKey as unknown[]
                if (queryKey.length >= 2 && queryKey[0] === 'get') {
                    const path = queryKey[1]
                    return (
                        path === '/api/v1/organizations/{orgCode}/issues' ||
                        path === '/api/v1/organizations/{orgCode}/issues/{issueUid}' ||
                        path === '/api/v1/organizations/{orgCode}/issues/stats'
                    )
                }
                return false
            },
        })
    }, [queryClient])

    const updateMutation = $api.useMutation(
        'patch',
        '/api/v1/organizations/{orgCode}/issues/{issueUid}',
        {
            onMutate: () => {
                if (isMountedRef.current) setSaveStatus('saving')
            },
            onSuccess: () => {
                if (isMountedRef.current) {
                    setSaveStatus('saved')
                    setTimeout(() => {
                        if (isMountedRef.current) setSaveStatus('idle')
                    }, 2000)
                }
                invalidateIssues()
            },
            onError: (err: unknown) => {
                if (isMountedRef.current) setSaveStatus('idle')
                // The server knows of children or a proof the page did not
                // show: offer the explicit close instead of failing silently.
                const code = errorCode(err)
                if (isMountedRef.current && (code === 'ISSUE_HAS_OPEN_CHILDREN' || code === 'STEP_AWAITS_PROOF')) {
                    setCloseOpen(true)
                }
            },
        },
    )

    const patchIssue = useCallback(
        (body: Record<string, unknown>) => {
            updateMutation.mutate({
                params: { path: { orgCode, issueUid } },
                body: body as any,
            })
        },
        [updateMutation, orgCode, issueUid],
    )

    const prevDebouncedDesc = useRef(descriptionValue)
    useEffect(() => {
        if (
            editingDescription &&
            issue &&
            debouncedDescription !== prevDebouncedDesc.current &&
            debouncedDescription !== (issue.description || '')
        ) {
            patchIssue({ description: debouncedDescription })
        }
        prevDebouncedDesc.current = debouncedDescription
    }, [debouncedDescription, editingDescription, issue, patchIssue])

    useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
        }
    }, [])

    const handleTitleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault()
                if (titleValue.trim() && titleValue !== issue?.title) {
                    patchIssue({ title: titleValue.trim() })
                }
                setEditingTitle(false)
            } else if (e.key === 'Escape') {
                // Radix Sheet listens for Escape on document via a native listener,
                // so synthetic stopPropagation isn't enough. Stop the native event too.
                e.preventDefault()
                e.stopPropagation()
                e.nativeEvent.stopImmediatePropagation()
                setTitleValue(issue?.title || '')
                setEditingTitle(false)
            }
        },
        [titleValue, issue?.title, patchIssue],
    )

    const handleTitleBlur = useCallback(() => {
        if (titleValue.trim() && titleValue !== issue?.title) {
            patchIssue({ title: titleValue.trim() })
        }
        setEditingTitle(false)
    }, [titleValue, issue?.title, patchIssue])

    const handleDescriptionBlur = useCallback(() => {
        if (descriptionValue !== (issue?.description || '')) {
            patchIssue({ description: descriptionValue })
        }
        setEditingDescription(false)
    }, [descriptionValue, issue?.description, patchIssue])

    // MarkdownEditor already stops the native Escape keydown from bubbling to
    // the enclosing Radix Sheet — this just reverts the value, as the plain
    // <Textarea> used to via its onKeyDown.
    const handleDescriptionEscape = useCallback(() => {
        setDescriptionValue(issue?.description || '')
        setEditingDescription(false)
    }, [issue?.description])

    const handleStatusChange = useCallback(
        (value: string) => {
            if (value === 'closed' && issue && closeNeedsDialog(issue)) {
                setCloseOpen(true)
                return
            }
            patchIssue({ status: value })
        },
        [patchIssue, issue],
    )

    const handleFollowUpChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value
            // A follow-up is a day: 09:00 local, when the manager is at work.
            patchIssue({ follow_up_at: value ? new Date(`${value}T09:00:00`).toISOString() : null })
        },
        [patchIssue],
    )

    const handlePriorityChange = useCallback(
        (value: string) => {
            patchIssue({ priority: parseInt(value, 10) })
        },
        [patchIssue],
    )

    const handleAssigneeChange = useCallback(
        (value: string) => {
            patchIssue({ assigned_to_uid: value === 'unassigned' ? undefined : value })
        },
        [patchIssue],
    )

    const handleDueDateChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value
            if (value) {
                patchIssue({ due_date: new Date(value).toISOString() })
            } else {
                patchIssue({ due_date: null })
            }
        },
        [patchIssue],
    )

    const targetLabel = useIssueTargetLabel(orgCode, issue?.target_entity_type, issue?.target_entity_uid)

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
        )
    }

    if (!issue) {
        return (
            <div className="flex flex-col items-center justify-center h-64">
                <p className="text-sm text-muted-foreground">{t('detail.not_found')}</p>
            </div>
        )
    }

    const priorityInfo = PRIORITY_CONFIG[issue.priority] || PRIORITY_CONFIG[3]
    const isSystemManaged = issue.system_managed === true

    // A catalogue-typed ticket (spec 2026-07-18-01) carries a diagnostic
    // subtype, which turns its detail view into a triptych: problème / règle /
    // remédiation. Manual tickets (no subtype) keep the plain description card.
    const issueProps = issue.properties as Record<string, unknown> | undefined
    const suggestionCode = (issueProps?.suggestion_code as string | undefined) ?? undefined
    const subtypeEntry = issue.subtype ? byCode[issue.subtype] : undefined
    const isTyped = Boolean(subtypeEntry)

    // The ticket's close-action, driven by its catalogue subtype. open_claim
    // spawns a claim dossier; every other action records its outcome and closes
    // the ticket. Legacy coverage suggestions with no subtype fall back to
    // open_claim via their suggestion_code.
    const defaultAction = subtypeEntry?.default_action
        ?? (suggestionCode === 'COVERAGE_CANDIDATE' ? 'open_claim' : undefined)
    const isOpen = issue.status === 'open' || issue.status === 'in_progress'
    const actionBusy = openClaim.isPending || resolveAction.isPending
    const isSendEmailAction = defaultAction ? SEND_EMAIL_ACTIONS.has(defaultAction) : false
    const actionButton = isOpen && defaultAction && issue.type !== 'claim' && (
        <Button
            size="sm"
            disabled={actionBusy}
            onClick={() => {
                if (isSendEmailAction) {
                    // Draft-and-confirm: open the compose dialog instead of
                    // firing the action directly (spec 2026-07-18-01).
                    setComposeOpen(true)
                } else if (defaultAction === 'notify_occupants') {
                    // A broadcast: the notice dialog first, then the outcome.
                    setNoticeOpen(true)
                } else if (defaultAction === 'open_claim') {
                    openClaim.mutate({ params: { path: { orgCode, issueUid } } })
                } else {
                    resolveAction.mutate({ params: { path: { orgCode, issueUid } }, body: { action: defaultAction } })
                }
            }}
            data-testid="ticket-action-btn"
        >
            {actionBusy ? <AppIcon path={mdiLoading} size="sm" className="mr-1 motion-safe:animate-spin" /> : null}
            {t(`action.${defaultAction}`, { defaultValue: defaultAction })}
        </Button>
    )

    // A send_email action leaves a workflow trace on the ticket: who we wrote
    // to, and — since spec 2026-08-03-01 — what actually happened to the
    // message (queued / sent / failed), with the job that carries it. The
    // `sent_at` used to be written optimistically at queue time, so a mail that
    // never left looked exactly like one that did.
    const workflow = (issueProps?.workflow as (EmailDelivery & { subject?: string }) | undefined)

    const reference = formatIssueReference(issue.number, i18n.language)

    const titleBlock = (
        <div className="flex items-start gap-4">
            {titleLeading}
            <div className="flex-1 min-w-0">
                {/* The ticket's short reference, above the title rather than
                    inside it: the title is inline-editable and the reference is
                    not the manager's to change (spec 2026-09-13-01). */}
                {/* The status leads the panel, beside the reference: it is the
                    first thing a manager triages on, and for a non-closed
                    ticket the badge is also the one-click close. */}
                <div className="flex items-center gap-2 px-2 -mx-2">
                    <CloseStatusBadge
                        org={orgCode}
                        uid={issueUid}
                        status={issue.status}
                        testId="issue-status-badge"
                    />
                    {reference && (
                        <span
                            className="text-xs text-muted-foreground tabular-nums"
                            data-testid="issue-number"
                        >
                            {reference}
                        </span>
                    )}
                </div>
                {isSystemManaged ? (
                    <h1
                        className={
                            (compact
                                ? 'text-lg font-semibold'
                                : 'text-2xl font-bold') +
                            ' text-foreground rounded px-2 py-1 -mx-2 flex items-center gap-2 flex-wrap'
                        }
                        title={t('detail.system_managed_locked')}
                        data-testid="issue-title"
                    >
                        <span>{issue.title}</span>
                        <Badge
                            variant="outline"
                            data-testid="system-managed-badge"
                            title={t('detail.system_managed_locked')}
                        >
                            {t('detail.system_managed_badge')}
                        </Badge>
                        {issue.ai_delegable && (
                            <span
                                title="AI Delegable"
                                data-testid="ai-delegable-badge"
                            >
                                🤖
                            </span>
                        )}
                    </h1>
                ) : editingTitle ? (
                    <Input
                        ref={titleInputRef}
                        value={titleValue}
                        onChange={(e) => setTitleValue(e.target.value)}
                        onKeyDown={handleTitleKeyDown}
                        onBlur={handleTitleBlur}
                        className={
                            compact
                                ? 'text-lg font-semibold h-auto py-1 px-2'
                                : 'text-2xl font-bold h-auto py-1 px-2'
                        }
                        data-testid="title-input"
                    />
                ) : (
                    <h1
                        className={
                            (compact
                                ? 'text-lg font-semibold'
                                : 'text-2xl font-bold') +
                            ' text-foreground cursor-pointer hover:bg-muted/50 rounded px-2 py-1 -mx-2 transition-colors'
                        }
                        onClick={() => setEditingTitle(true)}
                        data-testid="issue-title"
                    >
                        {issue.title}
                        {issue.ai_delegable && (
                            <span
                                title="AI Delegable"
                                data-testid="ai-delegable-badge"
                                className="ml-2"
                            >
                                🤖
                            </span>
                        )}
                    </h1>
                )}
            </div>

            {/* Audience (spec 2026-09-04-03, decision 7). The same badge the
                documents carry: knowing whether a resident can see this issue
                is part of reading it, and only the toggle is gated on
                `issues.write` on THIS row. */}
            <div className="flex items-center flex-shrink-0">
                <VisibilityBadge
                    visibility={(issue as { visibility?: string | null }).visibility ?? 'internal'}
                    writePermission="issues.write"
                    entity={issue as { permissions?: string[] }}
                    onChange={(next) => patchIssue({ visibility: next })}
                    testId="issue-visibility-badge"
                />
            </div>

            <div
                className="flex items-center gap-1 text-sm text-muted-foreground flex-shrink-0 min-w-[80px] justify-end"
                data-testid="save-status"
            >
                {saveStatus === 'saving' && (
                    <>
                        <AppIcon path={mdiLoading} size="xs" className="motion-safe:animate-spin" />
                        <span data-testid="save-status-saving">{t('detail.saving')}</span>
                    </>
                )}
                {saveStatus === 'saved' && (
                    <>
                        <AppIcon path={mdiCheckCircle} size="xs" className="text-green-500" />
                        <span data-testid="save-status-saved">{t('detail.saved')}</span>
                    </>
                )}
            </div>
        </div>
    )

    // ── Box 1: Problème ─────────────────────────────────────────────
    // The problem statement. For a catalogue-typed ticket it is titled
    // "Problème" and gathers the supporting evidence (intervention proof,
    // address comparison) inline; a manual ticket keeps the plain, editable
    // "Description".
    const descriptionBlock = isSystemManaged ? (
        <div
            className="min-h-[80px] rounded text-sm"
            title={t('detail.system_managed_locked')}
            data-testid="description-display"
        >
            {issue.description ? (
                <div className="text-foreground prose prose-sm dark:prose-invert max-w-none">
                    <Markdown remarkPlugins={[remarkGfm]}>{issue.description}</Markdown>
                </div>
            ) : (
                <p className="text-muted-foreground italic">
                    {t('detail.system_managed_locked')}
                </p>
            )}
        </div>
    ) : editingDescription ? (
        <MarkdownEditor
            key={issue.uid}
            value={descriptionValue}
            onChange={setDescriptionValue}
            onBlur={handleDescriptionBlur}
            onEscape={handleDescriptionEscape}
            placeholder={t('detail.description_placeholder')}
            autoFocus
            minHeight="min-h-[200px]"
            data-testid="description-textarea"
        />
    ) : (
        <div
            className="min-h-[80px] cursor-pointer rounded p-3 -m-3 hover:bg-muted/50 transition-colors text-sm"
            onClick={() => setEditingDescription(true)}
            data-testid="description-display"
        >
            {issue.description ? (
                <div className="text-foreground prose prose-sm dark:prose-invert max-w-none">
                    <Markdown remarkPlugins={[remarkGfm]}>{issue.description}</Markdown>
                </div>
            ) : (
                <p className="text-muted-foreground italic">
                    {t('detail.description_click')}
                </p>
            )}
        </div>
    )

    // Suggestion issues (spec 2026-07-17-01) carry the correlation engine's
    // aggregate outcome: the measured gap plus matched intervention UIDs — the
    // clickable proof that turns the suggestion from an opinion into a dossier.
    const suggestionAggregate = (issue.properties as { aggregate?: { intervention_uids?: string[] } } | undefined)
        ?.aggregate
    const hasProof = issue.type === 'suggestion' && (suggestionAggregate?.intervention_uids?.length ?? 0) > 0

    // data_consistency issues carry both addresses (check=document_address_mismatch).
    const mismatchProps = issue.properties as
        | {
              check?: string
              document_uid?: string
              document_address?: MismatchAddress
              asset_address?: MismatchAddress
          }
        | undefined
    const hasAddressMismatch = issue.type === 'data_consistency' &&
        mismatchProps?.check === 'document_address_mismatch'
    const addressMismatchBlock = hasAddressMismatch && mismatchProps && (
        <div className="space-y-3" data-testid="address-mismatch-card">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <AppIcon path={mdiMapMarker} size="xs" />
                {t('data_consistency.card_title')}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">
                        {t('data_consistency.document_address')}
                    </label>
                    <p className="text-sm text-foreground" data-testid="mismatch-document-address">
                        {formatMismatchAddress(mismatchProps.document_address)}
                    </p>
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">
                        {t('data_consistency.asset_address')}
                    </label>
                    <p className="text-sm text-foreground" data-testid="mismatch-asset-address">
                        {formatMismatchAddress(mismatchProps.asset_address)}
                    </p>
                </div>
            </div>
            {mismatchProps.document_uid && (
                <IssueTargetLink orgCode={orgCode} type="document" uid={mismatchProps.document_uid} />
            )}
        </div>
    )

    // data_consistency issues carrying check=property_reconciliation (spec
    // 2026-07-24-04-declared-vs-observed-property-reconciliation.md): a
    // building-level declared property (e.g. "3 elevators") disagrees with the
    // observed equipment count. Rendering + the two resolution actions (adopt
    // observed / complete the inventory) live in ReconciliationCard.
    const reconciliationIssueProps = issue.properties as ReconciliationProps | undefined
    const isReconciliation =
        issue.type === 'data_consistency' && reconciliationIssueProps?.check === 'property_reconciliation'

    const reconciliationCard = isReconciliation && reconciliationIssueProps && (
        <ReconciliationCard
            orgCode={orgCode}
            issueUid={issueUid}
            properties={reconciliationIssueProps}
            onAdopted={invalidateIssues}
        />
    )

    const problemCard = (
        <Card data-testid="problem-card">
            <CardHeader>
                <CardTitle className="text-base">
                    {isTyped ? t('detail.problem', { defaultValue: 'Problème' }) : t('detail.description')}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {descriptionBlock}
                {hasProof && (
                    <>
                        <div className="border-t border-border" />
                        <SuggestionProofCard
                            orgCode={orgCode}
                            assetUid={issue.target_entity_uid ?? undefined}
                            aggregate={suggestionAggregate!}
                            embedded
                        />
                    </>
                )}
                {hasAddressMismatch && (
                    <>
                        <div className="border-t border-border" />
                        {addressMismatchBlock}
                    </>
                )}
            </CardContent>
        </Card>
    )

    // ── Box 1b: Constat ─────────────────────────────────────────────
    // What a human declared, when nobody detected anything: the observed
    // instant, the structured answers, the recording and the photographs.
    // Renders nothing for a detector-produced ticket.
    const declarationCard = (
        <DeclarationCard orgCode={orgCode} properties={(issueProps || {}) as DeclarationProperties} />
    )

    // ── Box 2: Règle ────────────────────────────────────────────────
    // The norm the ticket was judged against, with a link into the
    // "Règles de gestion" page. Only for catalogue-typed tickets with a
    // rule binding.
    const ruleCard = subtypeEntry?.rule_binding?.control ? (
        <TicketRuleCard orgCode={orgCode} subtype={subtypeEntry} properties={issueProps} />
    ) : null

    // ── Box 3: Remédiation & workflow ───────────────────────────────
    // The recommended close-action and, for a claim ticket, the dossier
    // assureur checklist (the workflow object). Shown when there is an action
    // to trigger or a dossier to display.
    const claimProps = issue.properties as ClaimDossier | undefined
    const hasClaimDossier = issue.type === 'claim' && (claimProps?.dossier?.length ?? 0) > 0
    // The recurrence ticket's second route: budget the replacement instead of
    // (or as well as) chasing the provider (spec 2026-08-24-01, lot 3). Offered
    // only where it means something — a suggestion about one equipment, anchored
    // on that equipment.
    const capexLink = issueProps?.capex as { worksite_uid?: string; plan_uid?: string } | undefined
    const worksPlanTargetUid =
        issue.subtype === 'service_recurrence' && issue.target_entity_type === 'asset'
            ? issue.target_entity_uid
            : undefined
    const worksPlanButton = worksPlanTargetUid && (
        <AddToWorksPlanButton
            orgCode={orgCode}
            issueUid={issueUid}
            assetUid={worksPlanTargetUid}
            existingWorksiteUid={capexLink?.worksite_uid}
            existingPlanUid={capexLink?.plan_uid}
        />
    )

    const remediationCard = (actionButton || hasClaimDossier || workflow || worksPlanButton) && (
        <Card data-testid="remediation-card">
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <AppIcon path={mdiShimmer} size="sm" />
                    {t('remediation.card_title', { defaultValue: 'Remédiation' })}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {workflow?.status && (
                    <div
                        className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-foreground space-y-2"
                        data-testid="remediation-workflow"
                    >
                        <div>
                            {workflow.sent_at
                                ? t('remediation.workflow_sent', {
                                      defaultValue: 'Email envoyé à {{to}} le {{date}} — en attente de réponse.',
                                      to: (workflow.to ?? []).join(', '),
                                      date: new Date(workflow.sent_at).toLocaleDateString(),
                                  })
                                : t('remediation.workflow_pending', {
                                      defaultValue: 'Email à destination de {{to}}.',
                                      to: (workflow.to ?? []).join(', '),
                                  })}
                        </div>
                        <EmailDeliveryChip org={orgCode} delivery={workflow} />
                    </div>
                )}
                {actionButton && (
                    <>
                        {defaultAction && (
                            <p className="text-sm text-muted-foreground" data-testid="remediation-hint">
                                {t(`remediation.hint.${defaultAction}`, {
                                    defaultValue: t(`action.${defaultAction}`, { defaultValue: defaultAction }),
                                })}
                            </p>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                            {actionButton}
                            {worksPlanButton}
                        </div>
                    </>
                )}
                {/* A recurrence ticket on a closed thread still deserves a route
                    to the plan — chasing the provider and budgeting the
                    replacement are not exclusive. */}
                {!actionButton && worksPlanButton}
                {hasClaimDossier && (
                    <>
                        {actionButton && <div className="border-t border-border" />}
                        <ClaimDossierCard orgCode={orgCode} claim={claimProps!} embedded />
                    </>
                )}
            </CardContent>
        </Card>
    )

    // ── Follow-up (spec 2026-10-27-19 § 4) ────────────────────────────
    // The sweep prepared the chase (properties.follow_up.draft); "Relancer"
    // opens the compose dialog on it, "Réponse reçue" clears the follow-up.
    const followUp = issueProps?.follow_up as
        | { last_action?: string; draft?: { action?: string; to?: string; subject?: string; body?: string } }
        | undefined
    const exhausted = issueProps?.follow_up_exhausted as { count?: number } | undefined
    const isFollowUpDue = isOpen && followUpDue(issue.follow_up_at)
    const chaseAction = followUp?.draft?.action || followUp?.last_action
        || (workflow as { action?: string } | undefined)?.action || defaultAction
    const canChase = Boolean(chaseAction && SEND_EMAIL_ACTIONS.has(chaseAction))
    const followUpBar = (isFollowUpDue || (exhausted && isOpen && !issue.follow_up_at)) && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm" data-testid="issue-follow-up-bar">
            <AppIcon path={mdiBellRingOutline} size="sm" />
            {isFollowUpDue ? (
                <Badge variant="outline" className="border-warning text-warning" data-testid="issue-follow-up-due">
                    {t('follow_up.due')}
                </Badge>
            ) : (
                <Badge variant="outline" className="border-destructive/40 text-destructive" data-testid="issue-follow-up-exhausted">
                    {t('follow_up.exhausted', { count: exhausted?.count ?? issue.follow_up_count ?? 0 })}
                </Badge>
            )}
            {isFollowUpDue && (
                <div className="ml-auto flex gap-2">
                    {canChase && (
                        <Button size="sm" onClick={() => setChasing(true)} data-testid="issue-follow-up-chase">
                            {t('follow_up.chase')}
                        </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => patchIssue({ follow_up_at: null })} data-testid="issue-follow-up-answered">
                        {t('follow_up.answered')}
                    </Button>
                </div>
            )}
        </div>
    )

    const composeDialog = defaultAction && isSendEmailAction && (
        <ActionComposeDialog
            orgCode={orgCode}
            issueUid={issueUid}
            issueTitle={issue.title}
            action={defaultAction}
            open={composeOpen}
            onOpenChange={setComposeOpen}
        />
    )
    const chaseDialog = canChase && chaseAction && (
        <ActionComposeDialog
            orgCode={orgCode}
            issueUid={issueUid}
            issueTitle={issue.title}
            action={chaseAction}
            open={chasing}
            onOpenChange={setChasing}
            initialSubject={followUp?.draft?.subject}
            initialBody={followUp?.draft?.body}
            initialTo={followUp?.draft?.to}
        />
    )

    // ── Parent and children (spec 2026-10-27-19 § 6) ─────────────────
    const parentChip = issue.parent && (
        <Link
            to="/$org/issues/$uid"
            params={{ org: orgCode, uid: issue.parent.uid }}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-xs text-muted-foreground hover:text-foreground"
            data-testid="issue-parent-link"
        >
            <AppIcon path={mdiFileTreeOutline} size="xs" />
            {t('children.parent_label')} {formatIssueReference(issue.parent.number, i18n.language)} {issue.parent.title}
        </Link>
    )
    const childrenCard = (
        <>
            {/* The remediation scenario of the ticket (spec 2026-10-28-15 § 10). */}
            <ScenarioPanel orgCode={orgCode} issue={issue} />
            <IssueChildrenCard orgCode={orgCode} parent={issue} onCloseParent={() => handleStatusChange('closed')} />
        </>
    )
    const noticesCard = issue.target_entity_type === 'asset' && (
        <IssueNoticesCard orgCode={orgCode} issueUid={issueUid} />
    )
    const familyDialogs = (
        <>
            <CloseIssueDialog orgCode={orgCode} issue={issue} open={closeOpen} onOpenChange={setCloseOpen} />
            {defaultAction === 'notify_occupants' && (
                <NoticeDialog
                    orgCode={orgCode}
                    issueUid={issueUid}
                    open={noticeOpen}
                    onOpenChange={setNoticeOpen}
                    onSent={() => resolveAction.mutate({ params: { path: { orgCode, issueUid } }, body: { action: 'notify_occupants' } })}
                />
            )}
            {chaseDialog}
        </>
    )

    const commentsCard = (
        <Card>
            <CardContent className="py-4 space-y-6">
                <IssueComments orgCode={orgCode} issueUid={issueUid} nbComments={issue.nb_comments ?? 0} />
            </CardContent>
        </Card>
    )

    // The resident side of the ticket (spec 2026-09-02-05): its own card,
    // ABOVE the internal comments and only when the ticket came from (or was
    // attributed to) a request — it is the origin of the work, so it reads
    // first, and it must not be mistaken for a comment. Internal discussion
    // stays in the comments; only what the resident saw or sent is in the
    // conversation, and that boundary is what makes the public page safe.
    // The component renders nothing when there is no thread, card included.
    const conversationsCard = <EntityConversations org={orgCode} issueUid={issueUid} card />

    // A closed ticket owes an answer to the threads it came from (spec
    // 2026-09-16-09). Rendered under the same conditions as the list above:
    // only while the ticket is closed, and only when a linked thread exists.
    const tellThreadsCard = (
        <ClosedTicketThreads
            org={orgCode}
            issueUid={issueUid}
            status={issue.status}
            closedAt={issue.closed_at}
        />
    )


    const hasTarget = Boolean(issue.target_entity_type && issue.target_entity_uid)

    const targetBlock = hasTarget && (
        <div className="space-y-1.5" data-testid="issue-target-block">
            <label className="text-xs font-medium text-muted-foreground" data-testid="issue-target-label">
                {targetLabel}
            </label>
            <IssueTargetLink
                orgCode={orgCode}
                type={issue.target_entity_type as TargetType}
                uid={issue.target_entity_uid!}
            />
        </div>
    )

    const sourceCard = <IssueSourceCard orgCode={orgCode} issue={issue} />

    const metadataCard = (
        <Card>
            <CardContent className="py-4 space-y-4">
                {hasTarget && (
                    <>
                        {targetBlock}
                        <div className="border-t border-border" />
                    </>
                )}

                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                        <AppIcon path={mdiFlagOutline} size="xs" />
                        {t('detail.status_label')}
                    </label>
                    <Select value={issue.status} onValueChange={handleStatusChange}>
                        <SelectTrigger data-testid="status-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {STATUS_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                    {t(`status.${opt.value}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                        <span
                            className={`inline-block w-2 h-2 rounded-full ${priorityInfo.color}`}
                        />
                        {t('detail.priority_label')}
                    </label>
                    <Select
                        value={String(issue.priority)}
                        onValueChange={handlePriorityChange}
                    >
                        <SelectTrigger data-testid="priority-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {Object.entries(PRIORITY_CONFIG).map(([value, config]) => (
                                <SelectItem key={value} value={value}>
                                    <span className="flex items-center gap-2">
                                        <span
                                            className={`inline-block w-2 h-2 rounded-full ${config.color}`}
                                        />
                                        {t(config.tKey)}
                                    </span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                        <AppIcon path={mdiAccount} size="xs" />
                        {t('detail.assignee_label')}
                    </label>
                    <Select
                        value={issue.assigned_to_uid || issue.assigned_to?.uid || 'unassigned'}
                        onValueChange={handleAssigneeChange}
                    >
                        <SelectTrigger data-testid="assignee-select">
                            <SelectValue placeholder={t('detail.unassigned')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="unassigned">
                                {t('detail.unassigned')}
                            </SelectItem>
                            {users.map((user) => (
                                <SelectItem key={user.uid} value={user.uid}>
                                    {user.name || user.email}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                        <AppIcon path={mdiCalendar} size="xs" />
                        {t('detail.due_date_label')}
                    </label>
                    <input
                        type="date"
                        value={toDateInputValue(issue.due_date)}
                        onChange={handleDueDateChange}
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-background file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                        data-testid="due-date-input"
                    />
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5" htmlFor="issue-follow-up-at">
                        <AppIcon path={mdiBellRingOutline} size="xs" />
                        {t('follow_up.label')}
                    </label>
                    <input
                        id="issue-follow-up-at"
                        type="date"
                        value={toDateInputValue(issue.follow_up_at)}
                        onChange={handleFollowUpChange}
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                        data-testid="issue-follow-up-at"
                    />
                </div>

                {/* Process classification (spec 2026-07-18-01): the MECE domain
                    axis, not the legacy conflated `type`. Read-only — the domain
                    and subtype are set by the detector that raised the ticket. */}
                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                        <AppIcon path={mdiTag} size="xs" />
                        {t('detail.domain_label', { defaultValue: 'Domaine' })}
                    </label>
                    <div className="text-sm text-foreground" data-testid="issue-domain">
                        {issue.domain ? (
                            <>
                                <span>{t(`ticket.domain_${issue.domain}`, { defaultValue: issue.domain })}</span>
                                {subtypeEntry && (
                                    <span className="text-muted-foreground">
                                        {' · '}
                                        {subtypeLabel(subtypeEntry, i18n.language)}
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className="text-muted-foreground">
                                {t('detail.domain_manual', { defaultValue: 'Ticket manuel' })}
                            </span>
                        )}
                    </div>
                </div>

                <div className="border-t border-border pt-4 mt-4" />

                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                        <AppIcon path={mdiAccount} size="xs" />
                        {t('detail.created_by_label')}
                    </label>
                    <p className="text-sm text-foreground" data-testid="issue-creator">
                        {issue.created_by
                            ? issue.created_by.name ||
                              issue.created_by.email ||
                              t('detail.unknown_creator')
                            : t('detail.system_creator')}
                    </p>
                </div>

                {issue.created_at && (
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                            <AppIcon path={mdiClockOutline} size="xs" />
                            {t('detail.created_at_label')}
                        </label>
                        <p className="text-sm text-muted-foreground">
                            {formatDateTime(issue.created_at)}
                        </p>
                    </div>
                )}

                {issue.updated_at && (
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                            <AppIcon path={mdiClockOutline} size="xs" />
                            {t('detail.updated_at_label')}
                        </label>
                        <p className="text-sm text-muted-foreground">
                            {formatDateTime(issue.updated_at)}
                        </p>
                    </div>
                )}
            </CardContent>
        </Card>
    )

    if (compact) {
        return (
            <div className="space-y-4">
                {parentChip}
                {titleBlock}
                {followUpBar}
                {sourceCard}
                {problemCard}
                {declarationCard}
                {reconciliationCard}
                {ruleCard}
                {remediationCard}
                {childrenCard}
                {noticesCard}
                {tellThreadsCard}
                {conversationsCard}
                {commentsCard}
                {metadataCard}
                {composeDialog}
                {familyDialogs}
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {parentChip}
            {titleBlock}
            {followUpBar}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    {sourceCard}
                    {problemCard}
                    {declarationCard}
                    {reconciliationCard}
                    {ruleCard}
                    {remediationCard}
                    {childrenCard}
                    {noticesCard}
                    {tellThreadsCard}
                    {conversationsCard}
                    {commentsCard}
                </div>
                <div className="space-y-4">{metadataCard}</div>
            </div>
            {composeDialog}
            {familyDialogs}
        </div>
    )
}
